import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  FileText,
  Flag,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  Upload,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEnhancedMutation } from '@/modules/common/application';
import { useUser } from '@/modules/common/application/context/user';
import {
  AddChatAttachmentToDocumentHubMutation,
  AddChatAttachmentToDocumentHubMutationVariables,
  ChatAttachmentStatus,
  ChatReasoningEffort,
  ChatPermissionMode,
  MessageFeedbackType,
  MessageStatus,
  SenderType,
  ToolType,
} from '@/modules/common/types';
import type { SendMessageMutation } from '@/modules/common/types';
import { sendMessageToSlack } from '@/services/slack.service';

import { buildDocumentDetailPath } from '../../documents/utils/document-route';
import { ADD_CHAT_ATTACHMENT_TO_DOCUMENT_HUB } from '../apis/message.mutation';
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE,
  CHAT_ATTACHMENT_MAX_TOTAL_SIZE_BYTES,
  formatChatAttachmentSize,
  getChatAttachmentContentType,
} from '../application/chat-attachment-file-types';
import { readStoredChatPermissionMode } from '../application/chat-permission';
import {
  getActiveApprovalCall,
  getWorkLogProcessSteps,
  isActiveStep,
} from '../application/chat-work-log';
import type {
  ChatMessage,
  ChatMessageWithProcessSteps,
  ChatToolCall,
} from '../application/chat-work-log';
import { useChatAttachmentUpload } from '../application/use-chat-attachment-upload';
import {
  ChatAttachmentUploadError,
  type PreparedChatAttachment,
} from '../application/use-chat-attachment-upload';
import { useMessage } from '../application/use-message';
import type { ChatRequestContext } from '../types/request-context';
import { hasChatRequestContext } from '../types/request-context';
import { AssistantWorkLog } from './AssistantWorkLog';
import { ChatMarkdown } from './ChatMarkdown';
import { PromptBox, PromptBoxAttachment } from './PromptBox';
import { ReportIssueDialog } from './ReportIssueDialog';

interface ChatConversationContainerProps {
  conversationId: string;
  initialMessages?: SendMessageMutation['sendMessage']['messages'] | null;
  projectId?: string | null;
  requestContext?: ChatRequestContext | null;
  onRequestContextConsumed?: () => void;
  variant?: 'page' | 'panel';
}

type PendingChatAttachment = PromptBoxAttachment & {
  attachment?: PreparedChatAttachment;
};

type MessageAttachment = NonNullable<
  SendMessageMutation['sendMessage']['messages'][number]['attachments']
>[number];

type PromotedDocumentsByAttachmentId = Record<string, string>;

function createLocalAttachmentId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getAttachmentErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'Upload failed.';
}

function ChatAttachmentChips({
  attachments,
  onAddToDocumentHub,
  onOpenDocument,
  promotedDocumentsByAttachmentId,
  promotingAttachmentIds,
}: {
  attachments: MessageAttachment[];
  onAddToDocumentHub: (attachment: MessageAttachment) => void;
  onOpenDocument: (documentId: string, projectId: string) => void;
  promotedDocumentsByAttachmentId: PromotedDocumentsByAttachmentId;
  promotingAttachmentIds: Set<string>;
}) {
  const { t } = useTranslation();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mb-2 flex flex-wrap justify-end gap-2">
      {attachments.map((attachment) => {
        const documentId =
          attachment.documentId ?? promotedDocumentsByAttachmentId[attachment.id] ?? null;
        const canPromote = attachment.status === ChatAttachmentStatus.UPLOADED && !documentId;
        const isPromoting = promotingAttachmentIds.has(attachment.id);

        return (
          <div
            key={attachment.id}
            className="flex min-w-0 max-w-full items-center gap-2 rounded-md border bg-background px-2 py-1 text-xs text-foreground shadow-sm"
          >
            <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 max-w-48 truncate" title={attachment.originalName}>
              {attachment.originalName}
            </span>
            <span className="shrink-0 text-muted-foreground">
              {formatChatAttachmentSize(attachment.sizeBytes)}
            </span>
            {documentId ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 hover:bg-gray-100"
                    aria-label={t('modules.mavue2.chat.openAttachmentDocument')}
                    onClick={() => onOpenDocument(documentId, attachment.projectId)}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('modules.mavue2.chat.openAttachmentDocument')}</p>
                </TooltipContent>
              </Tooltip>
            ) : canPromote ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 hover:bg-gray-100"
                    disabled={isPromoting}
                    aria-label={t('modules.mavue2.chat.addAttachmentToDocumentHub')}
                    onClick={() => onAddToDocumentHub(attachment)}
                  >
                    <Upload className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('modules.mavue2.chat.addAttachmentToDocumentHub')}</p>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function getPreviousUserMessage(messages: ChatMessage[], messageIndex: number) {
  for (let index = messageIndex - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.sender === SenderType.USER) {
      return message;
    }
  }

  return null;
}

function getAssistantTurnUiState(message: ChatMessageWithProcessSteps) {
  const toolCalls = message.toolCalls ?? [];
  const workLogProcessSteps = getWorkLogProcessSteps({ message, toolCalls });
  const hasToolHistory = workLogProcessSteps.length > 0;
  const activeApprovalCall = getActiveApprovalCall({
    toolCalls,
    processSteps: workLogProcessSteps,
  });
  const hasActiveTool =
    !!activeApprovalCall || workLogProcessSteps.some((step) => isActiveStep(step));
  const isPending = message.status === MessageStatus.IN_PROGRESS;
  const isFailed = message.status === MessageStatus.FAILED;
  const hasResponse = message.content.trim().length > 0;
  const isThinking = isPending && !hasActiveTool && !hasResponse;
  const isWorking = isPending && hasActiveTool && !hasResponse;

  return {
    hasResponse,
    hasToolHistory,
    isThinking,
    isWorking,
    showAnswer: !isFailed && hasResponse,
    showThinking: isThinking,
    showWorkLog: hasToolHistory && !isFailed,
    workLogOpen: isThinking || isWorking,
    workLogTicking: isThinking || isWorking,
  };
}

function ThinkingIndicator() {
  const { t } = useTranslation();
  return (
    <div className="mb-2 text-xs">
      <span
        className="bg-clip-text text-transparent"
        style={{
          backgroundImage:
            'linear-gradient(90deg, hsl(var(--muted-foreground)) 0%, hsl(var(--muted-foreground)) 40%, hsl(var(--foreground)) 50%, hsl(var(--muted-foreground)) 60%, hsl(var(--muted-foreground)) 100%)',
          backgroundSize: '200% 100%',
          animation: 'thinking-shimmer 2s linear infinite',
        }}
      >
        {t('modules.mavue2.chat.assistantWorking')}
      </span>
      <style>{`
        @keyframes thinking-shimmer {
          0%   { background-position: 100% 0; }
          100% { background-position: -100% 0; }
        }
      `}</style>
    </div>
  );
}

export function ChatConversationContainer({
  conversationId,
  initialMessages = null,
  projectId = null,
  requestContext = null,
  onRequestContextConsumed,
  variant = 'page',
}: ChatConversationContainerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMessageId, setReportMessageId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<PendingChatAttachment[]>([]);
  const [promotedDocumentsByAttachmentId, setPromotedDocumentsByAttachmentId] =
    useState<PromotedDocumentsByAttachmentId>({});
  const [promotingAttachmentIds, setPromotingAttachmentIds] = useState<Set<string>>(new Set());
  const hasInitializedScrollRef = useRef(false);
  const isPinnedToBottomRef = useRef(true);
  const forceScrollOnNextUpdateRef = useRef(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { discardAttachment, uploadFile } = useChatAttachmentUpload({ conversationId, projectId });
  const [addChatAttachmentToDocumentHub] = useEnhancedMutation<
    AddChatAttachmentToDocumentHubMutation,
    AddChatAttachmentToDocumentHubMutationVariables
  >(ADD_CHAT_ATTACHMENT_TO_DOCUMENT_HUB);
  const hasProjectUrl = location.pathname.startsWith('/project/');

  const {
    messages,
    sendMessage,
    sendMessageLoading,
    sendMessageFeedback,
    respondToChatToolApproval,
    respondToChatToolApprovalLoading,
  } = useMessage({
    conversation: {
      id: conversationId,
      title: null,
      updatedAt: new Date().toISOString(),
      isCompleted: false,
    },
    setConversation: null,
    initialMessages: initialMessages ?? undefined,
  });
  const messagesScrollSignature = messages
    .map((message) => {
      const messageWithProcessSteps = message as ChatMessageWithProcessSteps;
      const toolCallSignature = (message.toolCalls ?? [])
        .map((call) =>
          [
            call.id,
            call.state,
            call.approved,
            call.output ? 'output' : '',
            call.errorText ? 'error' : '',
          ].join(':')
        )
        .join(',');
      const processStepSignature = (messageWithProcessSteps.processSteps ?? [])
        .map((step) => [step.id, step.status, step.completedAt ?? ''].join(':'))
        .join(',');

      return [
        message.id,
        message.status,
        message.content.length,
        (message.attachments ?? []).map((attachment) => attachment.id).join(','),
        toolCallSignature,
        processStepSignature,
      ].join('|');
    })
    .join('||');
  const hasBlockingAttachment = pendingAttachments.some(
    (attachment) => attachment.status !== 'uploaded'
  );

  useEffect(() => {
    hasInitializedScrollRef.current = false;
    isPinnedToBottomRef.current = true;
    forceScrollOnNextUpdateRef.current = true;
    setPendingAttachments([]);
    setPromotedDocumentsByAttachmentId({});
    setPromotingAttachmentIds(new Set());
  }, [conversationId]);

  const getDocumentPath = (documentId: string, attachmentProjectId: string) =>
    buildDocumentDetailPath({
      documentId,
      projectId: attachmentProjectId,
      hasProjectUrl,
    });

  const handleOpenAttachmentDocument = (documentId: string, attachmentProjectId: string) => {
    navigate(getDocumentPath(documentId, attachmentProjectId));
  };

  const handleAddAttachmentToDocumentHub = async (attachment: MessageAttachment) => {
    setPromotingAttachmentIds((current) => new Set(current).add(attachment.id));

    try {
      const result = await addChatAttachmentToDocumentHub({
        variables: {
          input: {
            attachmentId: attachment.id,
            projectId: attachment.projectId,
          },
        },
      });
      const document = result.data?.addChatAttachmentToDocumentHub;

      if (!document) {
        throw new Error('No document was returned.');
      }

      setPromotedDocumentsByAttachmentId((current) => ({
        ...current,
        [attachment.id]: document.id,
      }));
      toast.success(t('modules.mavue2.chat.attachmentAddedToDocumentHub'));
    } catch (error) {
      toast.error(t('modules.mavue2.chat.attachmentAddToDocumentHubFailed'), {
        description: getAttachmentErrorMessage(error),
      });
    } finally {
      setPromotingAttachmentIds((current) => {
        const next = new Set(current);
        next.delete(attachment.id);
        return next;
      });
    }
  };

  const uploadPendingAttachment = async (localId: string, file: File) => {
    try {
      const attachment = await uploadFile(file);

      setPendingAttachments((currentAttachments) =>
        currentAttachments.map((currentAttachment) =>
          currentAttachment.id === localId
            ? {
                ...currentAttachment,
                attachment,
                mimeType: attachment.mimeType,
                sizeBytes: attachment.sizeBytes,
                status: 'uploaded',
              }
            : currentAttachment
        )
      );
    } catch (error) {
      setPendingAttachments((currentAttachments) =>
        currentAttachments.map((currentAttachment) =>
          currentAttachment.id === localId
            ? {
                ...currentAttachment,
                attachment:
                  error instanceof ChatAttachmentUploadError && error.attachment
                    ? error.attachment
                    : currentAttachment.attachment,
                errorMessage: getAttachmentErrorMessage(error),
                status: 'error',
              }
            : currentAttachment
        )
      );
    }
  };

  const handleFilesSelected = (files: File[]) => {
    if (!projectId) {
      toast.error('Select a project before uploading chat attachments.');
      return;
    }

    const currentTotalSizeBytes = pendingAttachments.reduce(
      (total, attachment) => total + attachment.sizeBytes,
      0
    );
    let nextTotalSizeBytes = currentTotalSizeBytes;
    const remainingSlots = CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE - pendingAttachments.length;
    const selectedFiles = files.slice(0, Math.max(remainingSlots, 0));

    if (files.length > remainingSlots) {
      toast.error(`You can attach up to ${CHAT_ATTACHMENT_MAX_FILES_PER_MESSAGE} files.`);
    }

    const attachmentsToUpload = selectedFiles.flatMap((file) => {
      const mimeType = getChatAttachmentContentType(file);

      if (!mimeType) {
        toast.error(`${file.name} is not a supported file type.`);
        return [];
      }

      if (nextTotalSizeBytes + file.size > CHAT_ATTACHMENT_MAX_TOTAL_SIZE_BYTES) {
        toast.error('Chat attachments can be up to 50 MB combined.');
        return [];
      }

      nextTotalSizeBytes += file.size;

      return [
        {
          file,
          id: createLocalAttachmentId(),
          mimeType,
        },
      ];
    });

    if (attachmentsToUpload.length === 0) {
      return;
    }

    setPendingAttachments((currentAttachments) => [
      ...currentAttachments,
      ...attachmentsToUpload.map(({ file, id, mimeType }) => ({
        id,
        name: file.name,
        mimeType,
        sizeBytes: file.size,
        status: 'uploading' as const,
      })),
    ]);

    attachmentsToUpload.forEach(({ file, id }) => {
      void uploadPendingAttachment(id, file);
    });
  };

  const handleRemoveAttachment = (id: string) => {
    const attachmentToRemove = pendingAttachments.find((attachment) => attachment.id === id);

    setPendingAttachments((currentAttachments) =>
      currentAttachments.filter((attachment) => attachment.id !== id)
    );

    if (!attachmentToRemove?.attachment?.id) {
      return;
    }

    void discardAttachment(attachmentToRemove.attachment.id).catch((error) => {
      toast.error(getAttachmentErrorMessage(error));
    });
  };

  useEffect(() => {
    const scrollContainer = scrollAreaRef.current?.querySelector<HTMLElement>(
      '[data-radix-scroll-area-viewport]'
    );

    if (!scrollContainer) {
      return;
    }

    const updatePinnedState = () => {
      const distanceFromBottom =
        scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop;
      isPinnedToBottomRef.current = distanceFromBottom <= 160;
    };

    updatePinnedState();
    scrollContainer.addEventListener('scroll', updatePinnedState, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', updatePinnedState);
    };
  }, [conversationId]);

  useEffect(() => {
    if (!scrollAreaRef.current || messages.length === 0) {
      return;
    }

    const scrollContainer = scrollAreaRef.current.querySelector<HTMLElement>(
      '[data-radix-scroll-area-viewport]'
    );

    if (!scrollContainer) {
      return;
    }

    const scrollToBottom = () => {
      window.requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
        isPinnedToBottomRef.current = true;
      });
    };

    if (!hasInitializedScrollRef.current) {
      hasInitializedScrollRef.current = true;
      forceScrollOnNextUpdateRef.current = false;
      scrollToBottom();
      return;
    }

    if (!forceScrollOnNextUpdateRef.current && !isPinnedToBottomRef.current) {
      return;
    }

    forceScrollOnNextUpdateRef.current = false;
    scrollToBottom();
  }, [conversationId, messages.length, messagesScrollSignature]);

  const handleSendMessage = async (
    message: string,
    reasoningEffort: ChatReasoningEffort = ChatReasoningEffort.NONE,
    permissionMode: ChatPermissionMode = readStoredChatPermissionMode()
  ) => {
    if (hasBlockingAttachment) {
      toast.error('Wait for uploads to finish or remove failed attachments.');
      return;
    }

    const activeRequestContext = hasChatRequestContext(requestContext) ? requestContext : undefined;
    const attachmentIds = pendingAttachments
      .map((attachment) => attachment.attachment?.id)
      .filter((id): id is string => Boolean(id));
    forceScrollOnNextUpdateRef.current = true;
    try {
      await sendMessage({
        content: message,
        tool: ToolType.NORMAL,
        projectId,
        reasoningEffort,
        permissionMode,
        requestContext: activeRequestContext,
        attachmentIds,
      });
      setPendingAttachments([]);
    } finally {
      if (activeRequestContext) {
        onRequestContextConsumed?.();
      }
    }
  };

  const handleRetryMessage = async (messageIndex: number) => {
    const previousUserMessage = getPreviousUserMessage(messages, messageIndex);

    if (!previousUserMessage) {
      return;
    }

    forceScrollOnNextUpdateRef.current = true;
    await sendMessage({
      content: previousUserMessage.content,
      tool: ToolType.NORMAL,
      projectId,
      reasoningEffort: ChatReasoningEffort.NONE,
      permissionMode: readStoredChatPermissionMode(),
    });
  };

  const handleToolApproval = (messageId: string, call: ChatToolCall, approved: boolean) => {
    if (!call.approvalId) {
      return;
    }

    forceScrollOnNextUpdateRef.current = true;
    void respondToChatToolApproval({
      variables: {
        input: {
          messageId,
          approvalId: call.approvalId,
          approved,
          reason: approved ? 'Approved in Mavue chat' : 'Rejected in Mavue chat',
        },
      },
    });
  };

  const handleCopy = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast.success(t('modules.mavue2.chat.messageCopied'));

      setTimeout(() => {
        setCopiedMessageId(null);
      }, 3000);
    } catch {
      toast.error(t('modules.mavue2.chat.copyFailed'));
    }
  };

  const handleThumbsUp = (messageId: string) => {
    void sendMessageFeedback({
      variables: {
        messageId,
        type: MessageFeedbackType.THUMBS_UP,
      },
    });

    void sendMessageToSlack(
      `Message Reaction: Thumbs Up
User ${user?.firstName} ${user?.lastName}
Message ID: ${messageId}
Content: ${messages.find((message) => message.id === messageId)?.content}
`
    );
  };

  const handleThumbsDown = (messageId: string) => {
    void sendMessageFeedback({
      variables: {
        messageId,
        type: MessageFeedbackType.THUMBS_DOWN,
      },
    });

    void sendMessageToSlack(
      `Message Reaction: Thumbs Down
User ${user?.firstName} ${user?.lastName}
Message ID: ${messageId}`
    );
  };

  const handleReportIssue = (messageId: string) => {
    setReportMessageId(messageId);
    setReportDialogOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="flex h-full max-h-screen flex-col">
        <div className="flex-1 overflow-hidden">
          <ScrollArea ref={scrollAreaRef} className="h-full">
            <div className="flex w-full justify-center">
              <div className={`w-full px-4 ${variant === 'panel' ? 'max-w-none' : 'max-w-3xl'}`}>
                <div className="relative space-y-6 before:block before:h-6 before:content-[''] after:block after:h-6 after:content-['']">
                  {messages.map((message, messageIndex) => {
                    const isCompleted = message.status === MessageStatus.COMPLETED;
                    const isFailed =
                      message.sender === SenderType.BOT && message.status === MessageStatus.FAILED;
                    const retryMessage = isFailed
                      ? getPreviousUserMessage(messages, messageIndex)
                      : null;
                    const messageWithProcessSteps = message as ChatMessageWithProcessSteps;
                    const assistantTurn =
                      message.sender === SenderType.BOT
                        ? getAssistantTurnUiState(messageWithProcessSteps)
                        : null;
                    const shouldShowAssistantLabel =
                      message.sender === SenderType.BOT &&
                      !assistantTurn?.showThinking &&
                      !assistantTurn?.showWorkLog;

                    return (
                      <div
                        key={message.id}
                        className={`group flex ${
                          message.sender === SenderType.USER ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        {message.sender === SenderType.USER ? (
                          <div className="flex max-w-[75%] flex-col">
                            <ChatAttachmentChips
                              attachments={message.attachments ?? []}
                              onAddToDocumentHub={handleAddAttachmentToDocumentHub}
                              onOpenDocument={handleOpenAttachmentDocument}
                              promotedDocumentsByAttachmentId={promotedDocumentsByAttachmentId}
                              promotingAttachmentIds={promotingAttachmentIds}
                            />
                            <Card className="min-w-0 rounded-xl border-none bg-gray-100 px-3 py-2 shadow-none">
                              <ChatMarkdown variant="user">{message.content}</ChatMarkdown>
                            </Card>
                            <div className="mt-2 flex justify-end opacity-0 transition-opacity group-hover:opacity-100">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 hover:bg-gray-100"
                                    onClick={() => void handleCopy(message.content, message.id)}
                                  >
                                    {copiedMessageId === message.id ? (
                                      <Check className="h-3 w-3 text-green-600" />
                                    ) : (
                                      <Copy className="h-3 w-3" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t('modules.mavue2.chat.copyMessage')}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full max-w-[92%]">
                            {shouldShowAssistantLabel && (
                              <div className="mb-2 text-xs text-muted-foreground">
                                {t('modules.mavue2.navigation.assistant')}
                              </div>
                            )}

                            {assistantTurn?.showWorkLog && (
                              <AssistantWorkLog
                                message={messageWithProcessSteps}
                                open={assistantTurn.workLogOpen}
                                ticking={assistantTurn.workLogTicking}
                                disabled={respondToChatToolApprovalLoading}
                                onApproval={(toolCall, approved) =>
                                  handleToolApproval(message.id, toolCall, approved)
                                }
                                t={t}
                              />
                            )}

                            {assistantTurn?.showThinking && <ThinkingIndicator />}

                            {isFailed ? (
                              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-950">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                  <div className="min-w-0">
                                    <p className="font-medium">
                                      {t('modules.mavue2.chat.responseFailedTitle')}
                                    </p>
                                    {message.content ? (
                                      <p className="mt-1 text-red-900">{message.content}</p>
                                    ) : null}
                                  </div>
                                </div>

                                {retryMessage ? (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mt-3 h-8 border-red-200 bg-white text-red-900 hover:bg-red-100 hover:text-red-950"
                                    disabled={sendMessageLoading}
                                    onClick={() => void handleRetryMessage(messageIndex)}
                                  >
                                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                                    {t('modules.mavue2.chat.retryMessage')}
                                  </Button>
                                ) : null}
                              </div>
                            ) : assistantTurn?.showAnswer ? (
                              <ChatMarkdown className="transition-opacity duration-150">
                                {message.content}
                              </ChatMarkdown>
                            ) : null}

                            {isCompleted && (
                              <div className="mt-2 flex gap-1">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 hover:bg-gray-100"
                                      onClick={() => void handleCopy(message.content, message.id)}
                                    >
                                      {copiedMessageId === message.id ? (
                                        <Check className="h-3 w-3 text-green-600" />
                                      ) : (
                                        <Copy className="h-3 w-3" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('modules.mavue2.chat.copyMessage')}</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className={`h-6 w-6 hover:bg-gray-100 ${
                                        message.feedback?.type === 'THUMBS_UP'
                                          ? 'text-green-600'
                                          : 'text-black'
                                      }`}
                                      onClick={() => handleThumbsUp(message.id)}
                                    >
                                      <ThumbsUp className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('modules.mavue2.chat.goodResponse')}</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className={`h-6 w-6 hover:bg-gray-100 ${
                                        message.feedback?.type === 'THUMBS_DOWN'
                                          ? 'text-red-600'
                                          : 'text-black'
                                      }`}
                                      onClick={() => handleThumbsDown(message.id)}
                                    >
                                      <ThumbsDown className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('modules.mavue2.chat.poorResponse')}</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 text-black hover:bg-gray-100"
                                      onClick={() => handleReportIssue(message.id)}
                                    >
                                      <Flag className="h-3 w-3" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{t('modules.mavue2.chat.reportIssue')}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {messages.length === 0 && (
                    <div className="flex h-32 items-center justify-center text-gray-500">
                      <p className="text-sm">{t('modules.mavue2.chat.askQuestionToStart')}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ScrollArea>
        </div>

        <div className="w-full bg-white pb-4">
          <div
            className={`mx-auto w-full px-4 ${variant === 'panel' ? 'max-w-none' : 'max-w-3xl'}`}
          >
            <PromptBox
              onSubmit={handleSendMessage}
              attachments={pendingAttachments}
              fileInputAccept={CHAT_ATTACHMENT_ACCEPT}
              onFilesSelected={projectId ? handleFilesSelected : undefined}
              onRemoveAttachment={handleRemoveAttachment}
              disabled={sendMessageLoading}
              loading={sendMessageLoading}
              submitDisabled={hasBlockingAttachment}
            />
          </div>
        </div>

        <ReportIssueDialog
          open={reportDialogOpen}
          onOpenChange={setReportDialogOpen}
          messageId={reportMessageId || undefined}
        />
      </div>
    </TooltipProvider>
  );
}
